import {
  CreditCard as BillingIcon,
  History as SyncLogIcon,
  Inbox as MailboxIcon,
  LayoutDashboard as DashboardIcon,
  Settings as SettingsIcon,
  ShieldCheck as AdminIcon,
  Users as WaitlistIcon,
} from 'lucide-react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const navItems = [
  { to: '/dashboard', label: 'Dashboard', icon: DashboardIcon },
  { to: '/mailboxes', label: 'Mailboxes', icon: MailboxIcon },
  { to: '/sync-log', label: 'Sync Log', icon: SyncLogIcon },
  { to: '/settings', label: 'Settings', icon: SettingsIcon },
  { to: '/billing', label: 'Billing', icon: BillingIcon },
];

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

export function Sidebar({ open, onClose }: SidebarProps) {
  const { user } = useAuth();

  return (
    <>
      {/* Mobile overlay */}
      {open && (
        <div
          data-testid="sidebar-overlay"
          role="presentation"
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          onClick={onClose}
        />
      )}

      <aside
        className={`fixed top-16 left-0 z-40 flex h-[calc(100vh-4rem)] w-64 flex-col border-r border-slate-200 bg-white transition-transform lg:static lg:translate-x-0 ${
          open ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <nav className="flex-1 space-y-1 px-3 py-4">
          {navItems.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              onClick={onClose}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }`
              }
            >
              <Icon className="h-5 w-5 shrink-0" />
              <span>{label}</span>
            </NavLink>
          ))}
          {user.role === 'admin' && (
            <NavLink
              to="/admin/users"
              onClick={onClose}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-red-50 text-red-700'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }`
              }
            >
              <AdminIcon className="h-5 w-5" />
              <span>Admin</span>
            </NavLink>
          )}
          {user.role === 'admin' && (
            <NavLink
              to="/admin/waitlist"
              onClick={onClose}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-red-50 text-red-700'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }`
              }
            >
              <WaitlistIcon className="h-5 w-5" />
              <span>Waitlist</span>
            </NavLink>
          )}
        </nav>

        {user.plan === 'free' && (
          <div className="border-t border-slate-200 p-4">
            <div className="rounded-lg bg-blue-50 p-3">
              <p className="text-sm font-medium text-blue-900">Upgrade to Pro</p>
              <p className="mt-1 text-xs text-blue-700">Get 10 mailboxes and hourly sync</p>
              <NavLink
                to="/billing"
                onClick={onClose}
                className="mt-2 inline-block rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700"
              >
                Upgrade
              </NavLink>
            </div>
          </div>
        )}
      </aside>
    </>
  );
}
